import { useEffect, useRef, useState, useContext } from 'react';
import { PlaceContext } from '../contexts/PlaceContext';
import axios from 'axios';
import { URI } from '../config';

let triggerCacheLogged = {};

const NON_COMMERCIAL_TRIGGERS = {
  products: true,
  services: true,
  categories: true,
};

const sendLogs = async (logs) => {
  // console.log('Sending logs:', logs);
  axios.post(`${URI.logs}/api/views/add`, { logs }).catch((error) => {
    console.error('Error logging view:', error);
  });
};

const handleNonCommercialLogs = async () => {
  const logs = Object.values(triggerCacheLogged);

  if (!logs.length) {
    // console.log('No logs to send');
    return;
  }

  if (logs.length > 300) {
    console.log('Too many logs, not sending session', logs.length);
    return;
  }

  // console.log('Sending', logs.length, 'logs');
  await sendLogs(logs);
  // console.log('Done sending', logs.length, 'logs');

  triggerCacheLogged = {};
};

export const useInViewLogger = ({
  objectId,
  objectName,
  objectType,
  action = 'view',
  campaign,
  triggerInSeconds = 2,
  campaignId: incomingCampaignId,
  isActive = true,
}) => {
  const {
    data: {
      place: { placeId, campaignId },
      userId,
    },
  } = useContext(PlaceContext);

  const ref = useRef(null);
  const [hasLogged, setHasLogged] = useState(false);
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleNonCommercialLogs();
      }
    };

    const handlePageHide = () => {
      handleNonCommercialLogs();
    };

    const handleBlur = () => {
      handleNonCommercialLogs();
    };

    const handleFocus = () => {
      // Optionally, you can add logic for when the app regains focus
      // console.log('App regained focus');
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange, true);
    window.addEventListener('pagehide', handlePageHide, true); // Triggered when navigating away or closing the app
    window.addEventListener('blur', handleBlur, true);
    window.addEventListener('focus', handleFocus, true);

    // Cleanup event listeners on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange, true);
      window.removeEventListener('pagehide', handlePageHide, true);
      window.removeEventListener('blur', handleBlur, true);
      window.removeEventListener('focus', handleFocus, true);
    };
  }, [placeId, incomingCampaignId, campaignId]);

  useEffect(() => {
    if (!ref.current || hasLogged || !isActive) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (!hasLogged) {
              timeoutRef.current = setTimeout(() => {
                logView();
                setHasLogged(true);
              }, triggerInSeconds * 1000);
            }
          } else {
            // Reset timeout if user scrolls away before the threshold is met
            if (timeoutRef.current) {
              clearTimeout(timeoutRef.current);
            }
          }
        });
      },
      { threshold: 0.8 },
    );

    observer.observe(ref.current);

    return () => {
      observer.disconnect(); // Ensure observer resets when elements change
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [hasLogged, triggerInSeconds, isActive, placeId, incomingCampaignId, campaignId]);

  const logView = () => {
    if (!isActive) return; // Ensure logging is skipped if inactive

    const data = {
      place_id: placeId,
      object_id: objectId,
      object_name: objectName,
      object_type: objectType,
      action,
      action_details: { triggerInSeconds },
      time: new Date().toISOString(),
      user_id: userId,
      campaign_id: incomingCampaignId ? incomingCampaignId : campaignId || null,
    };

    // Prevent duplicate logging
    if (triggerCacheLogged[objectId] && NON_COMMERCIAL_TRIGGERS[objectType]) {
      return;
    } else if (NON_COMMERCIAL_TRIGGERS[objectType]) {
      // console.log('Caching', data.object_name); // Log to console for testing
      triggerCacheLogged[objectId] = data;
    }

    if (!NON_COMMERCIAL_TRIGGERS[objectType]) {
      // send commercial logs immediately
      // console.log('Sending commercial log:', data); // Log to console for testing
      sendLogs([data]);
    }
  };

  return ref; // Return the ref to attach to an element
};
